import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { SourcingRecommendationView } from '@app/src/pages/SourcingRecommendation/sourcingRecommendation'
import { AmplitudeTrackingEvents, DefaultImages } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { ArrowForward, Close, DataSaverOffOutlined } from '@mui/icons-material'
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'
import { buttonResetSx, NAV_ITEM_HEIGHT, NAV_ITEM_PX, navLabelSx, RAIL_WIDTH_TRANSITION_MS } from './navItemStyles'
import { useVerticalMenu } from './VerticalMenuContext'

const prefix = 'transparencyOverview.sourcingPromo'

const SourcingPromoCard = (): JSX.Element | null => {
  const { formatMessage } = useIntl()
  const { trackEvent } = useAmplitude()
  const history = useHistory()
  const { isExpanded } = useVerticalMenu()
  const [isDismissed, setIsDismissed] = useState(false)
  const [showFullCard, setShowFullCard] = useState(isExpanded)

  useEffect(() => {
    if (!isExpanded) {
      setShowFullCard(false)
      return
    }
    const timer = setTimeout(() => setShowFullCard(true), RAIL_WIDTH_TRANSITION_MS)
    return () => clearTimeout(timer)
  }, [isExpanded])

  const handleClose = (): void => {
    setIsDismissed(true)
  }

  const handleRestore = (): void => {
    setIsDismissed(false)
  }

  const handleClick = (): void => {
    trackEvent({ name: AmplitudeTrackingEvents.Transparency.SourcingPromo.LearnMoreClicked })
    history.push(generatePath(paths.sourcingRecommendation, { view: SourcingRecommendationView.Configure }))
  }

  if (isDismissed || !showFullCard) {
    const label = formatMessage({ id: isDismissed ? `${prefix}.collectSupplyChainData` : `${prefix}.understandRisk` })
    const handlePress = isDismissed ? handleRestore : handleClick

    return (
      <Box sx={{ m: 1 }}>
        <Tooltip arrow title={isExpanded ? '' : label} placement="right">
          <Box
            component="button"
            type="button"
            onClick={handlePress}
            aria-label={label}
            sx={[
              buttonResetSx,
              {
                display: 'flex',
                alignItems: 'center',
                minHeight: NAV_ITEM_HEIGHT,
                px: NAV_ITEM_PX,
                borderRadius: 2,
                cursor: 'pointer',
                bgcolor: 'grey.100',
                '&:hover': { backgroundColor: 'grey.200' },
              },
            ]}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                flexShrink: 0,
                bgcolor: 'success.light',
                color: 'secondary.main',
                borderRadius: 1.5,
              }}
            >
              <DataSaverOffOutlined />
            </Box>
            <Typography
              variant="subtitle2"
              color="text.primary"
              noWrap
              sx={[{ flexGrow: 1 }, navLabelSx(isExpanded), { p: 2 }]}
            >
              {label}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    )
  }

  return (
    <Stack
      sx={{
        p: 2,
        m: 1,
        bgcolor: 'grey.100',
        borderRadius: 2,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ position: 'relative', width: 48, height: 48, mt: -4, ml: -2 }}>
          <Box
            sx={{
              position: 'absolute',
              zIndex: 0,
              top: 9,
              left: 2,
              width: 48,
              height: 48,
              p: 0.4,
              bgcolor: 'grey.100',
              borderRadius: 1.5,
              border: '2px solid',
              borderColor: 'common.white',
              transform: 'rotate(-12deg)',
            }}
          >
            <Box
              component="img"
              src={DefaultImages.SourcingPromoCardIso}
              alt=""
              sx={{ width: 38, height: 38, objectFit: 'contain' }}
            />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              zIndex: 1,
              top: 0,
              left: 50,
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1.5,
              bgcolor: 'error.light',
              border: '2px solid',
              borderColor: 'common.white',
              transform: 'rotate(14deg)',
            }}
          >
            <Box
              component="img"
              src={DefaultImages.SourcingPromoCardWarning}
              alt=""
              sx={{ width: 32, height: 32, objectFit: 'contain', transform: 'rotate(-15deg)' }}
            />
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label={formatMessage({ id: 'general.close' })}
          sx={{ bgcolor: 'grey.200', borderRadius: '50%', mb: 1 }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Stack>
      <Typography variant="subtitle2" color="text.primary">
        {formatMessage({ id: `${prefix}.understandRisk` })}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {formatMessage({ id: `${prefix}.collectRiskData` })}
      </Typography>
      <Button variant="text" endIcon={<ArrowForward />} onClick={handleClick} sx={{ alignSelf: 'flex-end' }}>
        {formatMessage({ id: `${prefix}.seeHowItWorks` })}
      </Button>
    </Stack>
  )
}

export default SourcingPromoCard
